import { defineStore } from "pinia";
import { createSyncRule, ALL_METHOD_VALUES } from "@/composables/frequency/multiHatConstants.js";
import { clampOccupancy, DEFAULT_SCENARIOS, OCCUPANCY_DEFAULT, resultKeysOf, scenarioById, scenariosFromLegacyMethods, } from "@/composables/frequency/frequencyScenarios.js";
import { agToplamiFromOzetler, cheapestScenarioId, normalizeHatOzeti, } from "@/composables/frequency/hatOzeti.js";
import { hatKarar as computeHatKarar, mixedKpis as computeMixedKpis, recommendedScenarioId, HAT_ALT, } from "@/composables/frequency/freqoptDecision.js";
import { clearHatOverride, createDefaultGlobalParams, DECISION_STORAGE_KEY, effectiveParams as computeEffectiveParams, expandParamsToRowFields, hashParams, overrideCount, parseDecisionState, pickOverrides, reduceHatRowsToParams, serializeDecisionState, setHatOverride, } from "@/composables/frequency/freqoptParams.js";
import { DEFAULT_COLLAPSED_GROUPS } from "@/composables/frequency/freqoptSummary.js";
import { KIYAS_BAZ, normalizeKiyasKaynagi, persistKiyasForSenaryo, rememberedKiyasKaynagi, resolveKiyasSenaryoId, } from "@/composables/frequency/freqoptKiyas.js";
import frequencyApi from "@/api/frequency.api";
let rowCounter = 0;
export function createHatParamRow(overrides = {}) {
    rowCounter += 1;
    return {
        id: `hat-${Date.now()}-${rowCounter}`,
        hatNo: "",
        gun: "HAFTAICI",
        doluluk: OCCUPANCY_DEFAULT,
        kapasite: 90,
        minFrekans: 2,
        aracTipi: "SOLO",
        metod: "method4_beta0",
        tripFlexEnabled: true,
        maxFlexMinutes: 3,
        flexMode: "AUTO",
        otomatik: true,
        typePolicy: "auto",
        type_policy: "auto",
        seferMaliyet: 8,
        aracMaliyet: 800,
        ...overrides,
    };
}
/** Çoklu hat frekans oturumu — parametreler, önbellek, aktif hat */
export const useBulkSessionStore = defineStore("bulkSession", {
    state: () => ({
        hatRows: [createHatParamRow()],
        syncRules: [],
        resultsCache: {},
        activeHatNo: null,
        selectedMethods: [...resultKeysOf(DEFAULT_SCENARIOS)],
        selectedScenarios: [...DEFAULT_SCENARIOS],
        bulkApplyMetod: "method4_beta0",
        allHats: false,
        hedefDoluluk: OCCUPANCY_DEFAULT,
        basitMaliyet: false,
        seferMaliyet: 8,
        aracMaliyet: 800,
        viewMode: "params",
        runId: null,
        runStatus: null,
        publishing: false,
        runCounts: { total: 0, done: 0, failed: 0, running: 0 },
        hatOzetleri: {},
        hatDurumlari: {},
        agToplami: null,
        focusScenarioId: null,
        compareOpen: false,
        summaryUi: {
            search: "",
            filters: [],
            collapsedGroups: [...DEFAULT_COLLAPSED_GROUPS],
            detailHatNo: null,
        },
        globalParams: createDefaultGlobalParams(),
        lineOverrides: {},
        selectedScenarioByHat: {},
        approvedHats: {},
        visitedHats: {},
        paramSignatureByHat: {},
        hatNotes: {},
        selectedHatNos: [],
        hatSelectionTouched: false,
        ubsHatList: [],
        durakCache: {},
        lastBatchSummary: null,
        kiyasKaynagi: KIYAS_BAZ,
        /** Son ağ koşusunda kilitlenen kıyas (özet meta); kurulum seçiminden ayrı */
        lastRunKiyasKaynagi: null,
        calismaSenaryoId: null,
        /** Aktif senaryo SSD son kayıt: hat_no → ISO */
        hatKayitAt: {},
        hatKayitSenaryoId: null,
        hatKayitLoading: false,
    }),
    getters: {
        analyzedHats(state) {
            return Object.keys(state.resultsCache).filter((hatNo) => state.resultsCache[hatNo]?.analysisData);
        },
        isMultiHatSession() {
            return this.analyzedHats.length > 1;
        },
        validHatRows: (state) => state.hatRows.filter((row) => String(row.hatNo ?? "").trim().length > 0),
        activeSyncRules(state) {
            return state.syncRules.filter((rule) => rule.Aktif === "Evet" || rule.Aktif === "EVET" || rule.Aktif === true);
        },
        hasActiveSync() {
            return this.activeSyncRules.length > 0;
        },
        isFullAnalyzeMode() {
            return this.selectedScenarios.length > 1 || this.selectedMethods.length > 1;
        },
        doneHatNos(state) {
            return Object.keys(state.hatOzetleri).filter((hatNo) => (state.hatDurumlari[hatNo]?.durum || state.hatOzetleri[hatNo]?.durum) === "done");
        },
        /** Katalog auto/constrained: A1→A2 için /frekans-optimizasyon gerekir (toplu API tek metod + tek tip). */
        rowsNeedCatalogOptimize() {
            return this.validHatRows.some((row) => {
                if (row.otomatik === false) {
                    const codes = row.allowedTypeCodes ?? row.allowed_type_codes ?? [];
                    const policy = String(row.type_policy ?? row.typePolicy ?? "");
                    if (policy === "classic")
                        return false;
                    return codes.length !== 1 && policy !== "manual";
                }
                const policy = String(row.type_policy ?? row.typePolicy ?? "auto");
                return policy !== "classic" && policy !== "manual";
            });
        },
        /**
         * Toplu API yalnız: tek metod + classic (veya tek tip manual) + (senkron veya çok hat).
         * Üstten birden fazla metod veya katalog karışık tip seçiliyse asla batch kullanılmaz —
         * aksi halde yalnız satırdaki `metod` asimetrik çözülür, diğer metodlar/karışık tip kaybolur.
         */
        shouldUseBatchApi() {
            if (this.selectedMethods.length !== 1)
                return false;
            if (this.rowsNeedCatalogOptimize)
                return false;
            return this.hasActiveSync || this.validHatRows.length > 1;
        },
        defaultRowMetod() {
            if (this.selectedMethods.length === 1)
                return this.selectedMethods[0];
            return this.bulkApplyMetod || "method4_beta0";
        },
        /** Ana ekranda seçilebilir metod numaraları (M6 her zaman açık) */
        allowedMethodNums() {
            if (this.analyzedHats.length === 0) {
                return [4, 6];
            }
            const nums = new Set([6]);
            this.selectedScenarios.forEach((id) => {
                if (id !== "mevcut")
                    nums.add(4);
            });
            this.selectedMethods.forEach((value) => {
                if (value.startsWith("method4"))
                    nums.add(4);
            });
            return Array.from(nums).sort((a, b) => a - b);
        },
        allowedBetas() {
            const fromScenarios = this.selectedScenarios
                .map((id) => scenarioById(id)?.beta)
                .filter((n) => n != null);
            if (fromScenarios.length)
                return fromScenarios;
            const betas = this.selectedMethods
                .filter((v) => v.startsWith("method4"))
                .map((v) => {
                const m = String(v).match(/beta(\d+)/i);
                return m ? Number(m[1]) / 100 : null;
            })
                .filter((n) => n != null);
            return betas.length > 0 ? betas : [0, 0.2];
        },
        allowedScenarioIds() {
            return this.selectedScenarios.length ? [...this.selectedScenarios] : [...DEFAULT_SCENARIOS];
        },
        mixedKpis() {
            return computeMixedKpis({
                hatNos: null,
                hatOzetleri: this.hatOzetleri,
                hatDurumlari: this.hatDurumlari,
                globalParams: this.globalParams,
                lineOverrides: this.lineOverrides,
                selectedScenarioByHat: this.selectedScenarioByHat,
                approvedHats: this.approvedHats,
                paramSignatureByHat: this.paramSignatureByHat,
            });
        },
    },
    actions: {
        addHatRow(overrides = {}) {
            const metod = overrides.metod || this.defaultRowMetod;
            this.hatRows.push(createHatParamRow({ metod, ...overrides }));
        },
        /** Seçilen hat numaralarını satır listesiyle senkronize eder (parametreleri korur). */
        syncHatSelection(hatNos, template) {
            const wanted = [
                ...new Set((hatNos || [])
                    .map((n) => String(n ?? "").trim())
                    .filter(Boolean)),
            ];
            const byNo = new Map(this.hatRows
                .filter((r) => String(r.hatNo ?? "").trim())
                .map((r) => [String(r.hatNo).trim(), r]));
            const metod = template?.metod || this.defaultRowMetod;
            const next = wanted.map((hatNo) => {
                const existing = byNo.get(hatNo);
                if (existing)
                    return existing;
                return createHatParamRow({
                    metod,
                    gun: template?.gun || "HAFTAICI",
                    doluluk: template?.doluluk ?? this.hedefDoluluk ?? OCCUPANCY_DEFAULT,
                    kapasite: template?.kapasite ?? 90,
                    minFrekans: template?.minFrekans ?? 2,
                    aracTipi: template?.aracTipi || "SOLO",
                    tripFlexEnabled: template?.tripFlexEnabled ?? true,
                    maxFlexMinutes: template?.maxFlexMinutes ?? 3,
                    flexMode: template?.flexMode || "AUTO",
                    typePolicy: this.basitMaliyet ? "classic" : template?.typePolicy || "auto",
                    type_policy: this.basitMaliyet ? "classic" : template?.type_policy || template?.typePolicy || "auto",
                    otomatik: this.basitMaliyet ? template?.otomatik : template?.otomatik ?? true,
                    seferMaliyet: template?.seferMaliyet ?? this.seferMaliyet,
                    aracMaliyet: template?.aracMaliyet ?? this.aracMaliyet,
                    hatNo,
                });
            });
            this.hatRows = next.length > 0 ? next : [createHatParamRow({ metod })];
        },
        removeHatRow(id) {
            if (this.hatRows.length <= 1) {
                const only = this.hatRows[0];
                if (only) {
                    only.hatNo = "";
                }
                return;
            }
            this.hatRows = this.hatRows.filter((row) => row.id !== id);
        },
        setHatRows(rows) {
            this.hatRows = rows.length > 0 ? rows : [createHatParamRow()];
        },
        resetHatRows() {
            this.hatRows = [createHatParamRow({ metod: this.defaultRowMetod })];
            this.syncRules = [];
            this.selectedScenarios = [...DEFAULT_SCENARIOS];
            this.selectedMethods = [...resultKeysOf(DEFAULT_SCENARIOS)];
            this.bulkApplyMetod = "method4_beta0";
            this.allHats = false;
            this.basitMaliyet = false;
            this.seferMaliyet = 8;
            this.aracMaliyet = 800;
            this.hedefDoluluk = OCCUPANCY_DEFAULT;
            this.globalParams = createDefaultGlobalParams();
            this.lineOverrides = {};
            this.selectedScenarioByHat = {};
            this.approvedHats = {};
            this.visitedHats = {};
            this.paramSignatureByHat = {};
            this.hatNotes = {};
            this.summaryUi.detailHatNo = null;
            this.summaryUi.collapsedGroups = [...DEFAULT_COLLAPSED_GROUPS];
            this.selectedHatNos = [];
            this.hatSelectionTouched = false;
            this.viewMode = "params";
            this.runId = null;
            this.runStatus = null;
            this.runCounts = { total: 0, done: 0, failed: 0, running: 0 };
            this.hatOzetleri = {};
            this.hatDurumlari = {};
            this.agToplami = null;
            this.focusScenarioId = null;
            this.compareOpen = false;
            this.lastBatchSummary = null;
            this.lastRunKiyasKaynagi = null;
        },
        effectiveParams(hatNo) {
            return computeEffectiveParams(this.globalParams, this.lineOverrides, hatNo);
        },
        hatKarar(hatNo) {
            const key = String(hatNo || "").trim();
            const durum = this.hatDurumlari[key] || {};
            return computeHatKarar({
                hatNo: key,
                ozet: this.hatOzetleri[key],
                runDurum: durum.durum,
                globalParams: this.globalParams,
                lineOverrides: this.lineOverrides,
                selectedScenarioByHat: this.selectedScenarioByHat,
                approvedHats: this.approvedHats,
                storedSignature: this.paramSignatureByHat[key],
                publishedAt: durum.published_at ?? null,
                publishedSenaryoId: durum.published_senaryo_id ?? null,
            });
        },
        patchGlobalParams(patch) {
            this.globalParams = createDefaultGlobalParams({ ...this.globalParams, ...patch });
            this.hedefDoluluk = this.globalParams.doluluk;
            this.basitMaliyet = Boolean(this.globalParams.basitMaliyet);
            this.seferMaliyet = this.globalParams.seferMaliyet;
            this.aracMaliyet = this.globalParams.aracMaliyet;
            if (patch.kiyas_kaynagi != null) {
                this.kiyasKaynagi = normalizeKiyasKaynagi(patch.kiyas_kaynagi);
            }
            this.persistDecisionState();
        },
        setKiyasKaynagi(kaynak, ctx = {}) {
            const aktif = ctx.aktifSenaryo || null;
            let next = normalizeKiyasKaynagi(kaynak);
            const sid = aktif?.id ?? this.calismaSenaryoId;
            const kiyasSenaryoId = resolveKiyasSenaryoId(next, {
                aktifSenaryo: aktif,
                senaryolar: ctx.senaryolar || [],
                bulk: this,
            });
            // son_yayin uygun değilse otomatik baz
            if (next === "son_yayin" && (kiyasSenaryoId == null || Number(kiyasSenaryoId) !== Number(sid))) {
                next = KIYAS_BAZ;
            }
            this.kiyasKaynagi = next;
            this.calismaSenaryoId = sid ?? null;
            this.patchGlobalParams({
                kiyas_kaynagi: next,
                kiyas_senaryo_id: resolveKiyasSenaryoId(next, {
                    aktifSenaryo: aktif,
                    senaryolar: ctx.senaryolar || [],
                    bulk: this,
                }),
            });
            if (sid != null)
                persistKiyasForSenaryo(sid, next);
        },
        syncKiyasForSenaryo(ctx = {}) {
            const aktif = ctx.aktifSenaryo || null;
            const sid = aktif?.id ?? null;
            this.calismaSenaryoId = sid;
            const kaynak = rememberedKiyasKaynagi(sid, aktif, this);
            this.setKiyasKaynagi(kaynak, ctx);
        },
        setLineOverride(hatNo, patch) {
            const key = String(hatNo || "").trim();
            const tentative = setHatOverride(this.lineOverrides, key, patch);
            const pruned = pickOverrides(computeEffectiveParams(this.globalParams, tentative, key), this.globalParams);
            const next = { ...this.lineOverrides };
            if (Object.keys(pruned).length)
                next[key] = pruned;
            else
                delete next[key];
            this.lineOverrides = next;
            this.persistDecisionState();
        },
        clearLineOverride(hatNo, fields) {
            this.lineOverrides = clearHatOverride(this.lineOverrides, hatNo, fields);
            this.persistDecisionState();
        },
        overrideCount(hatNo) {
            return overrideCount(this.lineOverrides, hatNo);
        },
        setSelectedScenarioForHat(hatNo, scenarioId) {
            const key = String(hatNo || "").trim();
            if (!key || !scenarioId)
                return;
            this.selectedScenarioByHat = { ...this.selectedScenarioByHat, [key]: scenarioId };
            this._syncHatKarar(key, { selected_scenario_id: scenarioId });
        },
        setSelectedScenarioForHats(hatNos, scenarioId) {
            if (!scenarioId)
                return;
            const next = { ...this.selectedScenarioByHat };
            const keys = [];
            (hatNos || []).forEach((hatNo) => {
                const key = String(hatNo || "").trim();
                if (key) {
                    next[key] = scenarioId;
                    keys.push(key);
                }
            });
            this.selectedScenarioByHat = next;
            if (!keys.length) {
                this.persistDecisionState();
                return;
            }
            keys.forEach((key) => this._syncHatKarar(key, { selected_scenario_id: scenarioId }));
        },
        approveHat(hatNo, scenarioId) {
            const key = String(hatNo || "").trim();
            if (!key)
                return;
            const id = scenarioId || this.selectedScenarioByHat[key] || recommendedScenarioId(this.hatOzetleri[key]);
            if (!id)
                return;
            this.selectedScenarioByHat = { ...this.selectedScenarioByHat, [key]: id };
            this.approvedHats = { ...this.approvedHats, [key]: { scenarioId: id } };
            this._syncHatKarar(key, { approved: true, selected_scenario_id: id });
        },
        unapproveHat(hatNo) {
            const key = String(hatNo || "").trim();
            if (!key || !this.approvedHats[key])
                return;
            const next = { ...this.approvedHats };
            delete next[key];
            this.approvedHats = next;
            this._syncHatKarar(key, { approved: false });
        },
        approveHats(hatNos) {
            const keys = (hatNos || []).map((hatNo) => String(hatNo || "").trim()).filter(Boolean);
            if (!keys.length)
                return;
            keys.forEach((hatNo) => {
                const id = this.selectedScenarioByHat[hatNo] || recommendedScenarioId(this.hatOzetleri[hatNo]);
                if (!id)
                    return;
                this.selectedScenarioByHat = { ...this.selectedScenarioByHat, [hatNo]: id };
                this.approvedHats = { ...this.approvedHats, [hatNo]: { scenarioId: id } };
            });
            this.persistDecisionState();
            this._syncBulkApprove(keys);
        },
        approveOneriHazir(hatNos) {
            const nos = hatNos || Object.keys(this.hatOzetleri);
            const ready = nos.filter((hatNo) => this.hatKarar(hatNo).alt === HAT_ALT.ONERI);
            this.approveHats(ready);
            return ready;
        },
        async publishApprovedHats(senaryoId) {
            const kosuId = this.runId;
            if (!kosuId)
                throw new Error("Aktif ağ koşusu yok");
            if (senaryoId == null || String(senaryoId).trim() === "") {
                throw new Error("Aktif çalışma senaryosu seçin");
            }
            const onayli = Object.keys(this.approvedHats || {}).filter(Boolean);
            if (!onayli.length)
                throw new Error("Yayınlanacak onaylı hat yok");
            this.publishing = true;
            try {
                const res = await frequencyApi.publishNetworkRunApproved(kosuId, senaryoId);
                if (!res?.success)
                    throw new Error(res?.error || "Yayınlama başarısız");
                // Yayın damgalarını yerel duruma yansıt
                const yazilan = res.yazilan || [];
                const nextDurum = { ...this.hatDurumlari };
                yazilan.forEach((row) => {
                    const hatNo = String(row.hat_no || "").trim();
                    if (!hatNo)
                        return;
                    nextDurum[hatNo] = {
                        ...(nextDurum[hatNo] || {}),
                        published_at: new Date().toISOString(),
                        published_senaryo_id: res.senaryo_id ?? senaryoId,
                    };
                });
                this.hatDurumlari = nextDurum;
                try {
                    await this.loadHatKayitlari(senaryoId, { force: true });
                }
                catch {
                    /* tarih cache yenilenemese yayın yine başarılı */
                }
                return res;
            }
            finally {
                this.publishing = false;
            }
        },
        async loadHatKayitlari(senaryoId, opts = {}) {
            if (senaryoId == null || String(senaryoId).trim() === "") {
                this.hatKayitAt = {};
                this.hatKayitSenaryoId = null;
                return {};
            }
            const sid = String(senaryoId);
            if (!opts.force && String(this.hatKayitSenaryoId ?? "") === sid) {
                return this.hatKayitAt;
            }
            this.hatKayitLoading = true;
            try {
                const res = await frequencyApi.fetchSenaryoHatKayitlari(senaryoId);
                if (!res?.success)
                    throw new Error(res?.error || "Kayıt özeti alınamadı");
                const map = {};
                Object.entries(res.hatlar || {}).forEach(([hat, iso]) => {
                    const key = String(hat || "").trim();
                    if (key && iso)
                        map[key] = String(iso);
                });
                this.hatKayitAt = map;
                this.hatKayitSenaryoId = senaryoId;
                return map;
            }
            finally {
                this.hatKayitLoading = false;
            }
        },
        setHatNote(hatNo, note) {
            const key = String(hatNo || "").trim();
            if (!key)
                return;
            const text = String(note ?? "");
            const next = { ...this.hatNotes };
            if (!text.trim())
                delete next[key];
            else
                next[key] = text;
            this.hatNotes = next;
            this._syncHatKarar(key, { note: text.trim() ? text : null });
        },
        applyPresetRows(hatRows, extras = {}) {
            const reduced = reduceHatRowsToParams(hatRows, {
                ...extras,
                hedefDoluluk: extras.hedefDoluluk ?? this.hedefDoluluk,
                gun: extras.gun ?? this.globalParams.gun,
                basitMaliyet: extras.basitMaliyet ?? this.basitMaliyet,
                seferMaliyet: extras.seferMaliyet ?? this.seferMaliyet,
                aracMaliyet: extras.aracMaliyet ?? this.aracMaliyet,
            });
            this.globalParams = reduced.globalParams;
            this.lineOverrides = reduced.lineOverrides;
            this.hedefDoluluk = reduced.globalParams.doluluk;
            this.basitMaliyet = Boolean(reduced.globalParams.basitMaliyet);
            this.seferMaliyet = reduced.globalParams.seferMaliyet;
            this.aracMaliyet = reduced.globalParams.aracMaliyet;
            this.setHatRows(expandParamsToRowFields(reduced.globalParams, reduced.lineOverrides, reduced.hatNos).map((fields) => createHatParamRow(fields)));
            this.persistDecisionState();
            return reduced;
        },
        expandOverridesToHatRows(hatNos) {
            return expandParamsToRowFields(this.globalParams, this.lineOverrides, hatNos).map((fields) => createHatParamRow(fields));
        },
        persistDecisionState() {
            if (typeof localStorage === "undefined")
                return;
            try {
                localStorage.setItem(DECISION_STORAGE_KEY, JSON.stringify(serializeDecisionState({
                    globalParams: this.globalParams,
                    lineOverrides: this.lineOverrides,
                    selectedScenarioByHat: this.selectedScenarioByHat,
                    approvedHats: this.approvedHats,
                    visitedHats: this.visitedHats,
                    paramSignatureByHat: this.paramSignatureByHat,
                    hatNotes: this.hatNotes,
                })));
            }
            catch {
                /* kota / gizli kip */
            }
        },
        _serverRunId() {
            const id = this.runId;
            if (id == null || id === "" || id === "demo")
                return null;
            const n = Number(id);
            return Number.isFinite(n) && n > 0 ? n : null;
        },
        applyHatKararSatiri(hat) {
            const no = String(hat?.hat_no || "").trim();
            if (!no || !hat)
                return;
            const hasKarar = "selected_scenario_id" in hat ||
                "approved" in hat ||
                "approved_at" in hat ||
                "note" in hat ||
                "published_at" in hat ||
                "published_senaryo_id" in hat;
            if (!hasKarar)
                return;
            if (hat.selected_scenario_id) {
                this.selectedScenarioByHat = {
                    ...this.selectedScenarioByHat,
                    [no]: String(hat.selected_scenario_id),
                };
            }
            if (typeof hat.approved === "boolean") {
                if (hat.approved) {
                    const id = String(hat.selected_scenario_id || this.selectedScenarioByHat[no] || "").trim();
                    if (id)
                        this.approvedHats = { ...this.approvedHats, [no]: { scenarioId: id } };
                }
                else {
                    const next = { ...this.approvedHats };
                    delete next[no];
                    this.approvedHats = next;
                }
            }
            if ("note" in hat) {
                const next = { ...this.hatNotes };
                const text = hat.note == null ? "" : String(hat.note);
                if (!text.trim())
                    delete next[no];
                else
                    next[no] = text;
                this.hatNotes = next;
            }
            if ("published_at" in hat || "published_senaryo_id" in hat) {
                this.hatDurumlari = {
                    ...this.hatDurumlari,
                    [no]: {
                        ...(this.hatDurumlari[no] || {}),
                        published_at: hat.published_at ?? (this.hatDurumlari[no] || {}).published_at ?? null,
                        published_senaryo_id: hat.published_senaryo_id ??
                            (this.hatDurumlari[no] || {}).published_senaryo_id ??
                            null,
                    },
                };
            }
        },
        _syncHatKarar(hatNo, body) {
            this.persistDecisionState();
            const runId = this._serverRunId();
            if (runId == null)
                return;
            void frequencyApi
                .patchNetworkRunHatDecision(runId, hatNo, body)
                .then((data) => {
                if (data?.hat)
                    this.applyHatKararSatiri(data.hat);
            })
                .catch(() => {
                /* yedek localStorage */
            });
        },
        _syncBulkApprove(hatNos) {
            const runId = this._serverRunId();
            if (runId == null)
                return;
            void frequencyApi
                .bulkApproveNetworkRunHats(runId, hatNos)
                .then((data) => {
                (data?.hatlar || []).forEach((hat) => this.applyHatKararSatiri(hat));
            })
                .catch(() => {
                /* yedek localStorage */
            });
        },
        hydrateDecisionState() {
            if (typeof localStorage === "undefined")
                return false;
            try {
                const parsed = parseDecisionState(JSON.parse(localStorage.getItem(DECISION_STORAGE_KEY) || "null"));
                if (!parsed)
                    return false;
                this.globalParams = parsed.globalParams;
                this.lineOverrides = parsed.lineOverrides;
                this.selectedScenarioByHat = parsed.selectedScenarioByHat;
                this.approvedHats = parsed.approvedHats;
                this.visitedHats = (parsed.visitedHats || {});
                this.paramSignatureByHat = parsed.paramSignatureByHat;
                this.hatNotes = parsed.hatNotes || {};
                this.hedefDoluluk = parsed.globalParams.doluluk;
                this.basitMaliyet = Boolean(parsed.globalParams.basitMaliyet);
                this.seferMaliyet = parsed.globalParams.seferMaliyet;
                this.aracMaliyet = parsed.globalParams.aracMaliyet;
                return true;
            }
            catch {
                return false;
            }
        },
        _syncMethodsFromScenarios() {
            this.selectedMethods = [...resultKeysOf(this.selectedScenarios)];
            this.bulkApplyMetod = this.selectedMethods[0] || "method4_beta0";
        },
        toggleScenario(id) {
            const idx = this.selectedScenarios.indexOf(id);
            if (idx >= 0) {
                if (this.selectedScenarios.length <= 1)
                    return;
                this.selectedScenarios.splice(idx, 1);
            }
            else {
                this.selectedScenarios.push(id);
                this.selectedScenarios = scenariosFromLegacyMethods(this.selectedScenarios);
            }
            this._syncMethodsFromScenarios();
        },
        setSelectedScenarios(ids) {
            const next = scenariosFromLegacyMethods(ids);
            this.selectedScenarios = next.length ? next : [...DEFAULT_SCENARIOS];
            this._syncMethodsFromScenarios();
        },
        setAllHats(value) {
            this.allHats = Boolean(value);
        },
        setSelectedHatNos(hatNos, { touched = true } = {}) {
            const next = [
                ...new Set((hatNos || [])
                    .map((n) => String(n ?? "").trim())
                    .filter(Boolean)),
            ];
            this.selectedHatNos = next;
            if (touched)
                this.hatSelectionTouched = true;
        },
        toggleSelectedHatNo(hatNo) {
            const key = String(hatNo || "").trim();
            if (!key)
                return;
            const set = new Set(this.selectedHatNos);
            if (set.has(key))
                set.delete(key);
            else
                set.add(key);
            this.selectedHatNos = Array.from(set);
            this.hatSelectionTouched = true;
        },
        clearHatSelection() {
            this.selectedHatNos = [];
            this.hatSelectionTouched = true;
        },
        setBasitMaliyet(value) {
            this.basitMaliyet = Boolean(value);
            this.globalParams = createDefaultGlobalParams({
                ...this.globalParams,
                basitMaliyet: this.basitMaliyet,
                typePolicy: this.basitMaliyet ? "classic" : "auto",
            });
            this.hatRows.forEach((row) => {
                if (this.basitMaliyet) {
                    row.typePolicy = "classic";
                    row.type_policy = "classic";
                    row.seferMaliyet = this.seferMaliyet;
                    row.aracMaliyet = this.aracMaliyet;
                }
                else {
                    row.typePolicy = "auto";
                    row.type_policy = "auto";
                    row.otomatik = true;
                }
            });
            this.persistDecisionState();
        },
        setClassicCosts(seferMaliyet, aracMaliyet) {
            if (Number.isFinite(Number(seferMaliyet))) {
                this.seferMaliyet = Number(seferMaliyet);
            }
            if (Number.isFinite(Number(aracMaliyet))) {
                this.aracMaliyet = Number(aracMaliyet);
            }
            this.globalParams = createDefaultGlobalParams({
                ...this.globalParams,
                seferMaliyet: this.seferMaliyet,
                aracMaliyet: this.aracMaliyet,
            });
            this.hatRows.forEach((row) => {
                row.seferMaliyet = this.seferMaliyet;
                row.aracMaliyet = this.aracMaliyet;
            });
            this.persistDecisionState();
        },
        setHedefDoluluk(value) {
            this.hedefDoluluk = clampOccupancy(value, this.hedefDoluluk);
            this.globalParams = createDefaultGlobalParams({
                ...this.globalParams,
                doluluk: this.hedefDoluluk,
            });
            this.persistDecisionState();
        },
        applyDolulukToAllRows(doluluk) {
            const n = clampOccupancy(doluluk ?? this.hedefDoluluk, this.hedefDoluluk);
            this.setHedefDoluluk(n);
            this.hatRows.forEach((row) => {
                row.doluluk = n;
            });
        },
        toggleMethod(methodValue) {
            const idx = this.selectedMethods.indexOf(methodValue);
            const allSelected = this.selectedMethods.length >= ALL_METHOD_VALUES.length;
            // "Tüm metodlar" durumundan tıklama → yalnızca o metod
            if (allSelected) {
                this.selectedMethods = [methodValue];
                this.bulkApplyMetod = methodValue;
                this.applyMetodToAllRows(methodValue);
                return;
            }
            if (idx >= 0) {
                if (this.selectedMethods.length <= 1)
                    return;
                this.selectedMethods.splice(idx, 1);
                return;
            }
            this.selectedMethods.push(methodValue);
            this.bulkApplyMetod = methodValue;
            if (this.selectedMethods.length === 1) {
                this.applyMetodToAllRows(methodValue);
            }
        },
        selectOnlyMethod(methodValue) {
            this.selectedMethods = [methodValue];
            this.bulkApplyMetod = methodValue;
            this.applyMetodToAllRows(methodValue);
        },
        selectAllMethods() {
            this.selectedMethods = [...ALL_METHOD_VALUES];
        },
        applyMetodToAllRows(metod) {
            const value = metod || this.bulkApplyMetod;
            this.bulkApplyMetod = value;
            this.hatRows.forEach((row) => {
                row.metod = value;
            });
        },
        setUbsHatList(list) {
            const map = new Map();
            (list || []).forEach((row) => {
                const hatNo = String(row.hat_no ?? "").trim();
                if (!hatNo || map.has(hatNo))
                    return;
                map.set(hatNo, {
                    hat_no: hatNo,
                    hat_adi: String(row.hat_adi || hatNo).trim(),
                });
            });
            this.ubsHatList = Array.from(map.values());
        },
        setDurakCache(hatNo, duraklar) {
            const key = String(hatNo).trim();
            // Yeni obje referansı — storeToRefs(durakCache) aboneleri yenilensin
            this.durakCache = { ...this.durakCache, [key]: duraklar };
        },
        getDuraklarForHat(hatNo, yon) {
            const key = String(hatNo).trim();
            const all = this.durakCache[key] || [];
            if (!yon)
                return all;
            const fold = (s) => String(s ?? "")
                .toLowerCase()
                .replace(/ğ/g, "g")
                .replace(/ü/g, "u")
                .replace(/ş/g, "s")
                .replace(/ı/g, "i")
                .replace(/ö/g, "o")
                .replace(/ç/g, "c")
                .trim();
            const yonNorm = fold(yon);
            const wantGidis = yonNorm === "gidis" || yonNorm === "1" || yonNorm === "0";
            return all.filter((d) => {
                const dy = fold(String(d.yon ?? ""));
                if (wantGidis) {
                    return dy === "gidis" || dy === "1" || dy === "0";
                }
                return dy === "donus" || dy === "2";
            });
        },
        addSyncRule(rule = {}) {
            // Yeni dizi referansı — Options API panellerinin computed'ı kesin yenilensin
            this.syncRules = [...this.syncRules, createSyncRule(rule)];
        },
        removeSyncRule(id) {
            this.syncRules = this.syncRules.filter((rule) => rule.id !== id);
        },
        setLastBatchSummary(summary) {
            this.lastBatchSummary = summary;
        },
        cacheSnapshot(snapshot) {
            const key = String(snapshot.hatNo).trim();
            this.resultsCache[key] = snapshot;
            if (!this.activeHatNo) {
                this.activeHatNo = key;
            }
        },
        setActiveHatNo(hatNo) {
            this.activeHatNo = hatNo ? String(hatNo).trim() : null;
        },
        clearDrillDownCache() {
            this.resultsCache = {};
            this.activeHatNo = null;
        },
        clearSession() {
            this.clearDrillDownCache();
        },
        getSnapshot(hatNo) {
            const key = String(hatNo).trim();
            return this.resultsCache[key] ?? null;
        },
        setViewMode(mode) {
            this.viewMode = mode;
        },
        setCompareOpen(open) {
            this.compareOpen = Boolean(open);
        },
        setFocusScenario(id) {
            this.focusScenarioId = id;
        },
        toggleSummaryFilter(id) {
            const idx = this.summaryUi.filters.indexOf(id);
            if (idx >= 0)
                this.summaryUi.filters.splice(idx, 1);
            else
                this.summaryUi.filters.push(id);
        },
        toggleSummaryGroup(id) {
            const key = String(id || "");
            if (!key)
                return;
            const idx = this.summaryUi.collapsedGroups.indexOf(key);
            if (idx >= 0)
                this.summaryUi.collapsedGroups.splice(idx, 1);
            else
                this.summaryUi.collapsedGroups.push(key);
        },
        markHatVisited(hatNo) {
            const key = String(hatNo || "").trim();
            if (!key || this.visitedHats[key])
                return;
            this.visitedHats = { ...this.visitedHats, [key]: true };
            this.persistDecisionState();
        },
        resetInboxVisit() {
            this.visitedHats = {};
            this.summaryUi.detailHatNo = null;
        },
        setDetailHatNo(hatNo) {
            const key = hatNo ? String(hatNo).trim() : null;
            this.summaryUi.detailHatNo = key;
            if (!key)
                return;
            this.markHatVisited(key);
            // Karar ekranında varsayılan: sistem önerisi (kullanıcı Mevcut'a geçebilir).
            if (!this.approvedHats[key]) {
                const recId = recommendedScenarioId(this.hatOzetleri[key]);
                if (recId) {
                    this.selectedScenarioByHat = { ...this.selectedScenarioByHat, [key]: recId };
                }
            }
        },
        /**
         * Yeni ağ koşusu: önceki özet/kuyruk temizlenir, seçili hatlar queued olarak eklenir.
         * Poll gelmeden özet paneli "Koşu yok" göstermesin diye satırlar hemen basılır.
         */
        beginNetworkRun(input) {
            const nos = [
                ...new Set((input.hatNos || [])
                    .map((n) => String(n ?? "").trim())
                    .filter(Boolean)),
            ];
            const adi = input.hatAdiByNo || {};
            const durumlar = {};
            nos.forEach((no) => {
                durumlar[no] = {
                    durum: "queued",
                    hat_adi: adi[no] ?? null,
                    hata: null,
                };
            });
            this.clearDrillDownCache();
            this.resetInboxVisit();
            this.hatOzetleri = {};
            this.hatDurumlari = durumlar;
            this.agToplami = null;
            this.focusScenarioId = null;
            this.approvedHats = {};
            this.selectedScenarioByHat = {};
            this.paramSignatureByHat = {};
            this.runId = input.runId;
            this.runStatus = "queued";
            this.runCounts = {
                total: Number(input.total) || nos.length,
                done: 0,
                failed: 0,
                running: 0,
            };
            if (input.kiyasKaynagi != null) {
                this.lastRunKiyasKaynagi = normalizeKiyasKaynagi(input.kiyasKaynagi);
            }
            this.hatSelectionTouched = false;
            this.viewMode = "summary";
            this.persistDecisionState();
        },
        upsertHatOzeti(ozet) {
            if (!ozet?.hat_no)
                return;
            const key = String(ozet.hat_no);
            const hadResult = Boolean(this.hatOzetleri[key]?.senaryolar);
            if (hadResult) {
                const approved = { ...this.approvedHats };
                delete approved[key];
                this.approvedHats = approved;
                if (this.visitedHats[key]) {
                    const visited = { ...this.visitedHats };
                    delete visited[key];
                    this.visitedHats = visited;
                }
                const recId = recommendedScenarioId(ozet);
                if (recId) {
                    this.selectedScenarioByHat = { ...this.selectedScenarioByHat, [key]: recId };
                }
            }
            const normalized = normalizeHatOzeti(ozet) || ozet;
            this.hatOzetleri = {
                ...this.hatOzetleri,
                [key]: { ...normalized, durum: ozet.durum || "done" },
            };
            this.hatDurumlari = {
                ...this.hatDurumlari,
                [key]: {
                    ...(this.hatDurumlari[key] || {}),
                    durum: "done",
                    hat_adi: ozet.hat_adi,
                },
            };
            this.paramSignatureByHat = {
                ...this.paramSignatureByHat,
                [key]: hashParams(this.effectiveParams(key)),
            };
            if (!this.selectedScenarioByHat[key]) {
                const recId = recommendedScenarioId(ozet);
                if (recId) {
                    this.selectedScenarioByHat = { ...this.selectedScenarioByHat, [key]: recId };
                }
            }
            this.recomputeAgToplami();
            this.persistDecisionState();
        },
        recomputeAgToplami() {
            const done = Object.values(this.hatOzetleri).filter((o) => {
                const row = o;
                return Boolean(row && (row.durum === "done" || row.senaryolar));
            });
            this.agToplami = agToplamiFromOzetler(done);
            if (!this.focusScenarioId || !(this.agToplami?.senaryolar || {})[this.focusScenarioId]) {
                this.focusScenarioId =
                    cheapestScenarioId(this.agToplami) || this.selectedScenarios[0] || null;
            }
        },
        ingestKosu(kosu) {
            if (!kosu)
                return;
            this.runId = kosu.id ?? this.runId;
            this.runStatus = String(kosu.durum || this.runStatus || "");
            let parametreler = {};
            if (kosu.parametreler && typeof kosu.parametreler === "object") {
                parametreler = kosu.parametreler;
            }
            else if (typeof kosu.parametreler === "string") {
                try {
                    const parsed = JSON.parse(kosu.parametreler);
                    if (parsed && typeof parsed === "object")
                        parametreler = parsed;
                }
                catch {
                    /* bozuk JSON */
                }
            }
            if (parametreler.kiyas_kaynagi != null) {
                this.lastRunKiyasKaynagi = normalizeKiyasKaynagi(parametreler.kiyas_kaynagi);
            }
            const hatlar = Array.isArray(kosu.hatlar) ? kosu.hatlar : [];
            let nextDurum = { ...this.hatDurumlari };
            hatlar.forEach((hat) => {
                const no = String(hat.hat_no || "");
                if (!no)
                    return;
                let ozet = hat.ozet;
                if (typeof ozet === "string") {
                    try {
                        ozet = JSON.parse(ozet);
                    }
                    catch {
                        ozet = null;
                    }
                }
                nextDurum = {
                    ...nextDurum,
                    [no]: {
                        ...(nextDurum[no] || {}),
                        durum: hat.durum,
                        hata: hat.hata,
                        hat_adi: hat.hat_adi,
                        published_at: hat.published_at ?? (nextDurum[no] || {}).published_at ?? null,
                        published_senaryo_id: hat.published_senaryo_id ??
                            (nextDurum[no] || {}).published_senaryo_id ??
                            null,
                    },
                };
                this.hatDurumlari = nextDurum;
                if (ozet && typeof ozet === "object") {
                    this.upsertHatOzeti({
                        ...ozet,
                        hat_no: no,
                        hat_adi: hat.hat_adi,
                    });
                    nextDurum = {
                        ...this.hatDurumlari,
                        [no]: {
                            ...(this.hatDurumlari[no] || {}),
                            durum: hat.durum || "done",
                            hata: hat.hata,
                            hat_adi: hat.hat_adi,
                        },
                    };
                    this.hatDurumlari = nextDurum;
                }
                this.applyHatKararSatiri(hat);
                nextDurum = { ...this.hatDurumlari };
            });
            const total = Number(kosu.hat_sayisi) || Object.keys(this.hatDurumlari).length;
            const done = Number(kosu.biten) || this.doneHatNos.length;
            const failed = Number(kosu.hatali) ||
                Object.values(this.hatDurumlari).filter((h) => h.durum === "failed").length;
            const running = Object.values(this.hatDurumlari).filter((h) => h.durum === "running").length;
            this.runCounts = { total, done, failed, running };
            if (kosu.ag_toplami && typeof kosu.ag_toplami === "object") {
                this.agToplami = kosu.ag_toplami;
            }
            else {
                this.recomputeAgToplami();
            }
            if (!this.focusScenarioId) {
                this.focusScenarioId =
                    cheapestScenarioId(this.agToplami) || this.selectedScenarios[0] || null;
            }
            this.persistDecisionState();
        },
        loadDemoSummary(demo) {
            this.runId = "demo";
            this.runStatus = "completed";
            this.hatOzetleri = { ...demo.hatOzetleri };
            this.hatDurumlari = { ...demo.hatDurumlari };
            this.runCounts = demo.runCounts;
            this.recomputeAgToplami();
            this.focusScenarioId =
                cheapestScenarioId(this.agToplami) || this.selectedScenarios[0] || "asim_20";
            this.viewMode = "summary";
        },
    },
});
